import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { Field } from "../components/forms/_shared";
import { usePersonalization } from "../lib/personalization";
import { useContent } from "../lib/content";
import BuyerCostCalculator from "./BuyerCostCalculator";
import { Save, Calculator, RefreshCw, GitCompareArrows, Plus, X, Trash2, HelpCircle } from "lucide-react";

const num = (v) => Number(v || 0);

// Plain-English explainer text for each cost row in the "Estimated" panel.
// Aimed at a first-time seller — no jargon, includes typical ranges so
// they can sanity-check a title-company quote or a buyer's ask. Empty
// entries fall back to the label alone (no tooltip icon rendered).
const COST_HELP = {
  listing_commission:
    "What you pay your listing agent (the person representing you). Usually 2.5\u20133% of the sale price, negotiated up front.",
  buyer_commission:
    "What the buyer\u2019s agent gets paid \u2014 traditionally by the seller. Since the 2024 NAR settlement this is negotiable: some sellers offer 2\u20133%, some offer nothing.",
  mortgage_payoff:
    "What you still owe the bank on your home loan. Wired straight to your lender at closing \u2014 you never see it.",
  concessions:
    "Money you agree to credit the buyer, often to help cover their closing costs, inspection repairs, or an interest-rate buy-down. Only budget this if you expect to negotiate.",
  home_warranty:
    "A 1-year policy on the home\u2019s appliances & systems some sellers offer as a buyer perk. Usually $500\u2013$800.",
  hoa_transfer:
    "If your community has an HOA, a one-time admin fee to update ownership records. Often $150\u2013$500 depending on the association.",
  title_fees:
    "The title company\u2019s fees for issuing the new deed and running the escrow account. Rough rule of thumb: ~0.5% of the sale price.",
  prorated_taxes:
    "Property taxes are billed annually but split by ownership. You owe the buyer credit for the portion of the year from Jan 1 through closing day \u2014 auto-calculated from your annual tax bill.",
  misc_credit:
    "Anything extra your agent added as a credit toward your proceeds \u2014 e.g., a buyer\u2019s survey they'll cover, an escrow refund.",
  misc_debit:
    "Anything extra your agent added as a cost \u2014 e.g., a solar-panel payoff, a specific repair credit, a homeowners\u2019 documents packet.",
};

// Small inline "?" that shows a plain-English tooltip on hover (desktop)
// or tap (mobile). Uses a controlled state so it's touch-friendly; the
// icon is a lightweight lucide HelpCircle. Kept local to NetSheet since
// no other page needs this vocabulary today.
function CostHelp({ text }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  // Dismiss on outside-click so a mobile user can tap another row's icon
  // (or anywhere on the page) without the previous one lingering.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [open]);
  return (
    <span ref={ref} style={{ position: "relative", display: "inline-flex", alignItems: "center", marginLeft: 6 }}>
      <button
        type="button"
        aria-label="What is this cost?"
        data-testid="cost-help-toggle"
        onClick={() => setOpen((v) => !v)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          background: "transparent", border: "none", padding: 0, cursor: "help",
          color: "var(--text-faint)", lineHeight: 1,
        }}
      >
        <HelpCircle size={13} />
      </button>
      {open && (
        <span
          role="tooltip"
          data-testid="cost-help-popover"
          style={{
            position: "absolute",
            bottom: "calc(100% + 8px)", left: "50%", transform: "translateX(-50%)",
            width: 260, maxWidth: "80vw",
            background: "#1C1917", color: "#F8F6F3",
            fontFamily: "Arial, sans-serif", fontSize: 12, lineHeight: 1.5, fontWeight: 400,
            padding: "10px 12px", borderRadius: 8,
            boxShadow: "0 12px 32px rgba(28,25,23,0.28)",
            zIndex: 10, textAlign: "left", whiteSpace: "normal",
            pointerEvents: "none",
          }}
        >
          {text}
        </span>
      )}
    </span>
  );
}

// Title / Escrow / Recording auto-formula: 0.5% of purchase price.
// (Seller can override the result manually.)
const calcTitleFee = (price) => (price > 0 ? price * 0.005 : 0);

// Prorated property taxes: seller credits buyer for Jan 1 through closing day.
const calcProratedTaxes = (annualTaxes, closingDate) => {
  if (!annualTaxes || !closingDate) return 0;
  const close = new Date(closingDate + "T00:00:00");
  if (Number.isNaN(close.getTime())) return 0;
  const yearStart = new Date(close.getFullYear(), 0, 1);
  const days = Math.max(0, Math.round((close - yearStart) / 86400000) + 1);
  const isLeap = ((close.getFullYear() % 4 === 0 && close.getFullYear() % 100 !== 0) || close.getFullYear() % 400 === 0);
  return (annualTaxes / (isLeap ? 366 : 365)) * days;
};

const BLANK = {
  purchase_price: "", mortgage_payoff: "", concessions: "0",
  listing_commission_pct: 3, buyer_commission_pct: "",
  home_warranty: "0", hoa_transfer: "0",
  title_fees: "", title_fees_override: false,
  annual_property_taxes: "", closing_date: "",
  prorated_taxes: "", prorated_taxes_override: false,
  misc_credit_label: "", misc_credit_amount: "",
  misc_debit_label: "", misc_debit_amount: "",
};

export default function NetSheet() {
  const { content } = useContent();
  // Buy-side clients see the buyer cost calculator instead of the seller
  // net sheet. Same nav entry, same URL — the page just renders whichever
  // twin is relevant to the client's active transaction. Split into an
  // inner component so hooks below aren't called conditionally.
  if (content?.side === "buy") {
    return <BuyerCostCalculator />;
  }
  return <SellerNetSheet />;
}


function SellerNetSheet() {
  const { p } = usePersonalization();
  const [mode, setMode] = useState("single"); // 'single' | 'compare'
  const [f, setF] = useState(BLANK);
  const [loaded, setLoaded] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const saveTimer = useRef(null);

  // Initial load: hydrate from saved draft, then fall back to admin-set defaults.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/forms/draft/net_sheet");
        if (!alive) return;
        // Endpoint returns {form_type, data: {...}, ...}. Pull the actual draft body.
        const draft = (data && data.data) ? data.data : null;
        // Hydrate base values (draft beats personalization, personalization beats blank).
        const next = { ...BLANK };
        if (draft) Object.assign(next, draft);
        // One-time migration: the field used to default to 3, so older drafts have a stale 3
        // even though the user wants the field blank. Clear it so the seller types intentionally.
        if (draft && (next.buyer_commission_pct === 3 || next.buyer_commission_pct === "3")) {
          next.buyer_commission_pct = "";
        }
        // Pull admin-set defaults only when the seller hasn't already typed a value.
        if (!next.annual_property_taxes && p.annual_property_taxes != null) {
          next.annual_property_taxes = String(p.annual_property_taxes);
        }
        if (!next.closing_date && p.net_sheet_closing_date) {
          next.closing_date = p.net_sheet_closing_date;
        }
        setF(next);
      } finally { if (alive) setLoaded(true); }
    })();
    return () => { alive = false; };
    // Re-run when personalization arrives so defaults populate correctly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.annual_property_taxes, p.net_sheet_closing_date]);

  // Auto-update title_fees whenever purchase price changes (unless overridden).
  useEffect(() => {
    if (!loaded || f.title_fees_override) return;
    const price = num(f.purchase_price);
    setF((prev) => ({ ...prev, title_fees: price > 0 ? calcTitleFee(price).toFixed(2) : "" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.purchase_price, f.title_fees_override, loaded]);

  // Auto-update prorated_taxes from annual + closing date (unless overridden).
  useEffect(() => {
    if (!loaded || f.prorated_taxes_override) return;
    const annual = num(f.annual_property_taxes);
    const v = annual > 0 && f.closing_date ? calcProratedTaxes(annual, f.closing_date) : 0;
    setF((prev) => ({ ...prev, prorated_taxes: v > 0 ? v.toFixed(2) : "" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.annual_property_taxes, f.closing_date, f.prorated_taxes_override, loaded]);

  // Debounced auto-save to the existing draft endpoint so values persist
  // and (because drafts are scoped per household) co-owners see the same numbers.
  useEffect(() => {
    if (!loaded) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await api.post("/forms/draft/net_sheet", { data: f });
        setSavedAt(new Date());
      } catch { /* swallow — surface only on demand */ }
    }, 700);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [f, loaded]);

  const calc = useMemo(() => {
    const price = num(f.purchase_price);
    const listingCommission = price * (num(f.listing_commission_pct) / 100);
    const buyerCommission = price * (num(f.buyer_commission_pct) / 100);
    const miscCredit = num(f.misc_credit_amount);
    const miscDebit = num(f.misc_debit_amount);
    const totalCosts =
      num(f.mortgage_payoff) + num(f.concessions) + listingCommission + buyerCommission +
      num(f.home_warranty) + num(f.hoa_transfer) + num(f.title_fees) + num(f.prorated_taxes) + miscDebit;
    return { listingCommission, buyerCommission, totalCosts, miscCredit, miscDebit, net: price - totalCosts + miscCredit };
  }, [f]);

  const fmt = (n) => n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

  const downloadPdf = async () => {
    try {
      // Save first so the PDF reflects current values, then generate.
      await api.post("/forms/draft/net_sheet", { data: f });
      const res = await api.post("/forms/net_sheet/pdf", { ...f, net_proceeds: calc.net }, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `net_sheet_${(p.property_address || "estimate").replace(/[^a-z0-9]+/gi, "_").toLowerCase()}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { /* no-op; the live calculator is the main UX */ }
  };

  const reset = () => {
    if (!window.confirm("Clear all values and start a fresh net sheet?")) return;
    setF({ ...BLANK });
  };

  return (
    <div className="container-x" style={{ padding: "48px 0 64px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12 }}>
        <div>
          <span className="overline">Net sheet</span>
          <h1 className="font-serif" style={{ fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 500, margin: "8px 0 12px", letterSpacing: "-0.01em" }}>
            What you'll walk away with.
          </h1>
          <p style={{ color: "var(--text-muted)", maxWidth: 720, fontSize: "1.05rem" }}>
            Punch in your numbers and watch your estimated proceeds update live. Your work auto-saves — come back any time.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start", flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={downloadPdf}
            data-testid="netsheet-pdf"
            className="btn-secondary"
            style={{ padding: "10px 16px", fontSize: 14, display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <Save size={14} /> Save as PDF
          </button>
          <button
            type="button"
            onClick={reset}
            data-testid="netsheet-reset"
            className="btn-ghost"
            style={{ padding: "10px 14px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <RefreshCw size={13} /> Reset
          </button>
        </div>
      </div>

      <div style={{ display: "flex", gap: 6, marginTop: 28, padding: 4, background: "var(--surface-alt)", borderRadius: 999, width: "fit-content" }} data-testid="ns-mode-toggle">
        <button
          type="button"
          data-testid="ns-mode-single"
          onClick={() => setMode("single")}
          className={mode === "single" ? "btn-primary" : "btn-ghost"}
          style={{ padding: "8px 18px", fontSize: 13, borderRadius: 999, display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <Calculator size={14} /> Single estimate
        </button>
        <button
          type="button"
          data-testid="ns-mode-compare"
          onClick={() => setMode("compare")}
          className={mode === "compare" ? "btn-primary" : "btn-ghost"}
          style={{ padding: "8px 18px", fontSize: 13, borderRadius: 999, display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <GitCompareArrows size={14} /> Compare offers
        </button>
      </div>

      {mode === "compare" ? (
        <CompareOffers shared={f} fmt={fmt} />
      ) : (
      <div className="card" style={{ padding: 32, display: "flex", flexDirection: "column", gap: 20, marginTop: 24 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 20 }}>
          <Field label="Purchase Price">
            <input data-testid="ns-price" className="input-field" type="number" value={f.purchase_price} onChange={upd("purchase_price")} placeholder="500000" />
          </Field>
          <Field label="Mortgage Payoff Balance">
            <input data-testid="ns-payoff" className="input-field" type="number" value={f.mortgage_payoff} onChange={upd("mortgage_payoff")} placeholder="250000" />
          </Field>
          <Field label="Seller Concessions" hint="Budget if buyer asks for help">
            <input data-testid="ns-concessions" className="input-field" type="number" value={f.concessions} onChange={upd("concessions")} />
          </Field>
          <Field label="Listing Commission (%)">
            <input data-testid="ns-listing-pct" className="input-field" type="number" step="0.1" value={f.listing_commission_pct} onChange={upd("listing_commission_pct")} />
          </Field>
          <Field label="Buyer Commission (%)" hint="Budget for 3%">
            <input data-testid="ns-buyer-pct" className="input-field" type="number" step="0.1" value={f.buyer_commission_pct} onChange={upd("buyer_commission_pct")} />
          </Field>
          <Field label="Home Warranty">
            <input data-testid="ns-warranty" className="input-field" type="number" value={f.home_warranty} onChange={upd("home_warranty")} />
          </Field>
          <Field label="HOA Transfer Fee">
            <input data-testid="ns-hoa" className="input-field" type="number" value={f.hoa_transfer} onChange={upd("hoa_transfer")} />
          </Field>
          <Field
            label="Title / Escrow / Recording"
            hint={f.title_fees_override ? "Manual override" : "Auto: 0.5% × purchase price"}
          >
            <input
              data-testid="ns-title"
              className="input-field"
              type="number"
              value={f.title_fees}
              onChange={(e) => setF({ ...f, title_fees: e.target.value, title_fees_override: true })}
              placeholder="Auto-calculated"
            />
            {f.title_fees_override && (
              <button
                type="button"
                data-testid="ns-title-reset"
                onClick={() => setF({ ...f, title_fees_override: false })}
                style={{ marginTop: 6, fontSize: 12, color: "var(--primary)", background: "none", border: "none", padding: 0, cursor: "pointer", textDecoration: "underline" }}
              >
                ↺ Use auto-calculation
              </button>
            )}
          </Field>
          <Field label="Annual Property Taxes" hint="Yearly amount on your tax bill">
            <input data-testid="ns-annual-taxes" className="input-field" type="number" value={f.annual_property_taxes} onChange={upd("annual_property_taxes")} placeholder="3500" />
          </Field>
          <Field label="Closing Date" hint="Used to prorate property taxes">
            <input data-testid="ns-closing-date" className="input-field" type="date" value={f.closing_date} onChange={upd("closing_date")} />
          </Field>
          <Field
            label="Prorated Property Taxes"
            hint={f.prorated_taxes_override ? "Manual override" : "Auto: annual × (days from Jan 1 to closing) ÷ days in year"}
          >
            <input
              data-testid="ns-taxes"
              className="input-field"
              type="number"
              value={f.prorated_taxes}
              onChange={(e) => setF({ ...f, prorated_taxes: e.target.value, prorated_taxes_override: true })}
              placeholder="Auto-calculated"
            />
            {f.prorated_taxes_override && (
              <button
                type="button"
                data-testid="ns-taxes-reset"
                onClick={() => setF({ ...f, prorated_taxes_override: false })}
                style={{ marginTop: 6, fontSize: 12, color: "var(--primary)", background: "none", border: "none", padding: 0, cursor: "pointer", textDecoration: "underline" }}
              >
                ↺ Use auto-calculation
              </button>
            )}
          </Field>
        </div>

        {(f.misc_credit_label || f.misc_credit_amount || f.misc_debit_label || f.misc_debit_amount) ? (
          <div className="card" style={{ padding: 20, background: "var(--surface-alt)", boxShadow: "none" }} data-testid="ns-misc-section">
            <div className="overline" style={{ marginBottom: 12 }}>Misc credits & debits</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
              <Field label="Credit label" hint="Things added to your proceeds">
                <input data-testid="ns-misc-credit-label" className="input-field" type="text" value={f.misc_credit_label} onChange={upd("misc_credit_label")} placeholder="e.g. Prepaid HOA" />
              </Field>
              <Field label="Credit amount">
                <input data-testid="ns-misc-credit-amount" className="input-field" type="number" value={f.misc_credit_amount} onChange={upd("misc_credit_amount")} />
              </Field>
              <Field label="Debit label" hint="Things subtracted from your proceeds">
                <input data-testid="ns-misc-debit-label" className="input-field" type="text" value={f.misc_debit_label} onChange={upd("misc_debit_label")} placeholder="e.g. Repair credit to buyer" />
              </Field>
              <Field label="Debit amount">
                <input data-testid="ns-misc-debit-amount" className="input-field" type="number" value={f.misc_debit_amount} onChange={upd("misc_debit_amount")} />
              </Field>
            </div>
          </div>
        ) : null}

        <div className="card" style={{ padding: 24, background: "var(--surface-alt)", boxShadow: "none" }} data-testid="ns-summary">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Calculator size={14} style={{ color: "var(--primary)" }} />
            <div className="overline">Estimated</div>
          </div>
          {/* Full cost breakdown — every line that rolls into Total Costs
              gets its own row (empty/$0 rows are hidden to keep it readable).
              Commissions always show even at 0 so the seller can tell they
              were considered. */}
          <SummaryRow label="Listing Commission" value={calc.listingCommission} fmt={fmt} testid="ns-summary-listing-commission" help={COST_HELP.listing_commission} />
          <SummaryRow label="Buyer Commission"   value={calc.buyerCommission}   fmt={fmt} testid="ns-summary-buyer-commission"   help={COST_HELP.buyer_commission} />
          <SummaryRow label="Mortgage Payoff"    value={num(f.mortgage_payoff)} fmt={fmt} testid="ns-summary-mortgage-payoff"   help={COST_HELP.mortgage_payoff} hideIfZero />
          <SummaryRow label="Seller Concessions" value={num(f.concessions)}     fmt={fmt} testid="ns-summary-concessions"       help={COST_HELP.concessions}     hideIfZero />
          <SummaryRow label="Home Warranty"      value={num(f.home_warranty)}   fmt={fmt} testid="ns-summary-home-warranty"     help={COST_HELP.home_warranty}   hideIfZero />
          <SummaryRow label="HOA Transfer Fee"   value={num(f.hoa_transfer)}    fmt={fmt} testid="ns-summary-hoa-transfer"      help={COST_HELP.hoa_transfer}    hideIfZero />
          <SummaryRow label="Title / Escrow / Recording" value={num(f.title_fees)} fmt={fmt} testid="ns-summary-title-fees"     help={COST_HELP.title_fees}     hideIfZero />
          <SummaryRow label="Prorated Property Taxes" value={num(f.prorated_taxes)} fmt={fmt} testid="ns-summary-prorated-taxes" help={COST_HELP.prorated_taxes} hideIfZero />
          {calc.miscDebit > 0 && (
            <SummaryRow
              label={f.misc_debit_label || "Misc debit"}
              value={calc.miscDebit}
              fmt={fmt}
              testid="ns-summary-misc-debit"
              help={COST_HELP.misc_debit}
            />
          )}
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10, paddingTop: 10, borderTop: "1px dashed var(--border)" }}>
            <span style={{ color: "var(--text)", fontWeight: 600 }}>Total Costs</span>
            <b data-testid="ns-summary-total-costs">{fmt(calc.totalCosts)}</b>
          </div>
          {calc.miscCredit > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }} data-testid="ns-summary-misc-credit">
              <span style={{ color: "var(--text-muted)", display: "inline-flex", alignItems: "center" }}>
                + {f.misc_credit_label || "Misc credit"}
                <CostHelp text={COST_HELP.misc_credit} />
              </span>
              <b style={{ color: "var(--primary)" }}>{fmt(calc.miscCredit)}</b>
            </div>
          )}
          <div className="gradient-divider" style={{ margin: "14px 0" }} />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <span className="font-serif" style={{ fontSize: 22 }}>Net Proceeds</span>
            <span className="font-serif" data-testid="ns-net-proceeds" style={{ fontSize: 32, color: "var(--primary)", fontWeight: 600 }}>{fmt(calc.net)}</span>
          </div>
          <p style={{ color: "var(--text-muted)", fontSize: 12, marginTop: 12, margin: "12px 0 0" }}>
            Estimate only — actual figures come from your title company at closing.
          </p>
        </div>

        <div style={{ color: "var(--text-muted)", fontSize: 12, textAlign: "right" }} data-testid="ns-saved-status">
          {savedAt ? `Saved ${savedAt.toLocaleTimeString()}` : "Auto-saves as you type"}
        </div>
      </div>
      )}
    </div>
  );
}


/* ---------- Compare offers ---------- */

// Single line-item row for the "Estimated" cost breakdown. Hides itself
// when `hideIfZero` and the value is 0, so the seller sees only the
// costs that actually apply to their sale. Optional `help` renders a
// small "?" icon next to the label with a plain-English explainer.
function SummaryRow({ label, value, fmt, testid, hideIfZero = false, help = "" }) {
  if (hideIfZero && (!value || value <= 0)) return null;
  return (
    <div data-testid={testid} style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
      <span style={{ color: "var(--text-muted)", display: "inline-flex", alignItems: "center" }}>
        {label}
        {help && <CostHelp text={help} />}
      </span>
      <b>{fmt(value || 0)}</b>
    </div>
  );
}


const BLANK_OFFER = {
  name: "",
  purchase_price: "",
  concessions: "0",
  buyer_commission_pct: "",
  closing_date: "",
};

function CompareOffers({ shared, fmt }) {
  const { p } = usePersonalization();
  const [offers, setOffers] = useState([
    { ...BLANK_OFFER, name: "Offer A" },
    { ...BLANK_OFFER, name: "Offer B" },
  ]);
  const [loaded, setLoaded] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const saveTimer = useRef(null);

  // Hydrate: merge admin-set offers (from personalization) with seller's saved draft.
  // Seller draft wins for any offer name they've edited; admin-only offers are appended.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/forms/draft/net_sheet_offers");
        if (!alive) return;
        const draft = data.draft?.data;
        const adminOffers = (p.offers || []).map((o) => ({ ...BLANK_OFFER, ...o }));
        let merged;
        if (draft && Array.isArray(draft.offers) && draft.offers.length) {
          const sellerNames = new Set(draft.offers.map((o) => (o.name || "").toLowerCase()));
          const newFromAdmin = adminOffers.filter((o) => !sellerNames.has((o.name || "").toLowerCase()));
          merged = [...draft.offers, ...newFromAdmin];
        } else if (adminOffers.length) {
          merged = adminOffers;
        } else {
          merged = [
            { ...BLANK_OFFER, name: "Offer A" },
            { ...BLANK_OFFER, name: "Offer B" },
          ];
        }
        setOffers(merged.slice(0, 3).map((o) => (
          o.buyer_commission_pct === 3 || o.buyer_commission_pct === "3"
            ? { ...o, buyer_commission_pct: "" }
            : o
        )));
      } finally { if (alive) setLoaded(true); }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.offers]);

  // Auto-save whenever offers change.
  useEffect(() => {
    if (!loaded) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await api.post("/forms/draft/net_sheet_offers", { data: { offers } });
        setSavedAt(new Date());
      } catch { /* swallow */ }
    }, 700);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [offers, loaded]);

  const setOffer = (idx, patch) => setOffers((xs) => xs.map((o, i) => i === idx ? { ...o, ...patch } : o));
  const addOffer = () => {
    if (offers.length >= 3) return;
    const letters = ["A", "B", "C"];
    setOffers((xs) => [...xs, { ...BLANK_OFFER, name: `Offer ${letters[xs.length] || xs.length + 1}` }]);
  };
  const removeOffer = (idx) => {
    if (offers.length <= 2) return;
    setOffers((xs) => xs.filter((_, i) => i !== idx));
  };

  // Each column inherits the seller's shared inputs but uses its own price/concessions/buyer-pct/closing.
  const columns = useMemo(() => offers.map((o) => {
    const price = num(o.purchase_price);
    const buyerCommission = price * (num(o.buyer_commission_pct) / 100);
    const listingCommission = price * (num(shared.listing_commission_pct) / 100);
    const titleFees = calcTitleFee(price);
    const proratedTaxes = calcProratedTaxes(num(shared.annual_property_taxes), o.closing_date || shared.closing_date);
    const totalCosts =
      num(shared.mortgage_payoff) + num(o.concessions) + listingCommission + buyerCommission +
      num(shared.home_warranty) + num(shared.hoa_transfer) + titleFees + proratedTaxes;
    return {
      offer: o,
      buyerCommission, listingCommission, titleFees, proratedTaxes, totalCosts,
      net: price - totalCosts,
    };
  }), [offers, shared]);

  const bestNet = Math.max(...columns.map((c) => c.net));
  const sharedReady = num(shared.mortgage_payoff) > 0 || num(shared.listing_commission_pct) > 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 24 }} data-testid="compare-panel">
      {!sharedReady && (
        <div className="card" style={{ padding: 16, fontSize: 14, color: "var(--text-muted)" }}>
          Tip: fill in your <b>shared inputs</b> on <button type="button" onClick={() => null} style={{ background: "none", border: "none", color: "var(--primary)", padding: 0, font: "inherit", cursor: "pointer", textDecoration: "underline" }}>Single estimate</button> first (mortgage payoff, listing commission, annual taxes). Each offer below pulls from those.
        </div>
      )}

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ padding: "16px 24px", background: "var(--surface-alt)", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <div>
            <div className="overline">Compare offers side-by-side</div>
            <div style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 4 }}>
              Each column uses your shared inputs from the Single estimate tab — only price, concessions, buyer commission, and closing date vary per offer.
            </div>
          </div>
          {offers.length < 3 && (
            <button
              type="button"
              data-testid="compare-add-offer"
              onClick={addOffer}
              className="btn-secondary"
              style={{ padding: "8px 14px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <Plus size={14} /> Add offer
            </button>
          )}
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: offers.length === 3 ? 720 : 540 }}>
            <thead>
              <tr>
                <th style={th}>Field</th>
                {offers.map((o, idx) => (
                  <th key={idx} style={{ ...th, position: "relative" }} data-testid={`compare-col-${idx}`}>
                    <input
                      data-testid={`compare-name-${idx}`}
                      className="input-field"
                      value={o.name}
                      onChange={(e) => setOffer(idx, { name: e.target.value })}
                      placeholder={`Offer ${String.fromCharCode(65 + idx)}`}
                      style={{ width: "100%", fontWeight: 600, fontSize: 15 }}
                    />
                    {offers.length > 2 && (
                      <button
                        type="button"
                        data-testid={`compare-remove-${idx}`}
                        onClick={() => removeOffer(idx)}
                        className="btn-ghost"
                        style={{ position: "absolute", top: 8, right: 8, padding: 4, color: "var(--secondary)" }}
                        title="Remove this offer"
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <Row label="Purchase Price">
                {offers.map((o, idx) => (
                  <td key={idx} style={td}>
                    <input data-testid={`compare-price-${idx}`} className="input-field" type="number" value={o.purchase_price} onChange={(e) => setOffer(idx, { purchase_price: e.target.value })} placeholder="500000" />
                  </td>
                ))}
              </Row>
              <Row label="Seller Concessions">
                {offers.map((o, idx) => (
                  <td key={idx} style={td}>
                    <input data-testid={`compare-concessions-${idx}`} className="input-field" type="number" value={o.concessions} onChange={(e) => setOffer(idx, { concessions: e.target.value })} />
                  </td>
                ))}
              </Row>
              <Row label="Buyer Commission (%)">
                {offers.map((o, idx) => (
                  <td key={idx} style={td}>
                    <input data-testid={`compare-buyer-pct-${idx}`} className="input-field" type="number" step="0.1" value={o.buyer_commission_pct} onChange={(e) => setOffer(idx, { buyer_commission_pct: e.target.value })} />
                    <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>Budget for 3%</div>
                  </td>
                ))}
              </Row>
              <Row label="Closing Date">
                {offers.map((o, idx) => (
                  <td key={idx} style={td}>
                    <input data-testid={`compare-closing-${idx}`} className="input-field" type="date" value={o.closing_date} onChange={(e) => setOffer(idx, { closing_date: e.target.value })} />
                    <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
                      {o.closing_date ? "" : `Falls back to: ${shared.closing_date || "—"}`}
                    </div>
                  </td>
                ))}
              </Row>

              <RowHeader label="Computed" />
              <ReadRow label="Listing Commission" cells={columns.map((c) => fmt(c.listingCommission))} />
              <ReadRow label="Buyer Commission" cells={columns.map((c) => fmt(c.buyerCommission))} />
              <ReadRow label="Title / Escrow / Recording" cells={columns.map((c) => fmt(c.titleFees))} />
              <ReadRow label="Prorated Property Taxes" cells={columns.map((c) => fmt(c.proratedTaxes))} />
              <ReadRow label="Total Costs" cells={columns.map((c) => fmt(c.totalCosts))} bold />

              <tr style={{ background: "var(--surface-alt)" }}>
                <td style={{ ...td, fontWeight: 600, fontSize: 16 }}>Net Proceeds</td>
                {columns.map((c, idx) => {
                  const isBest = c.net === bestNet && c.net > 0;
                  const delta = c.net - bestNet;
                  return (
                    <td key={idx} style={{ ...td, textAlign: "left" }} data-testid={`compare-net-${idx}`}>
                      <div className="font-serif" style={{ fontSize: 24, fontWeight: 600, color: isBest ? "var(--primary)" : "var(--text)" }}>
                        {fmt(c.net)}
                      </div>
                      {isBest ? (
                        <span className="badge badge-success" style={{ background: "var(--primary)", color: "#fff", marginTop: 6, display: "inline-block" }}>Best offer</span>
                      ) : delta < 0 ? (
                        <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>{fmt(delta)} vs best</div>
                      ) : null}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ color: "var(--text-muted)", fontSize: 12, textAlign: "right" }} data-testid="compare-saved-status">
        {savedAt ? `Saved ${savedAt.toLocaleTimeString()}` : "Auto-saves as you type"}
      </div>
    </div>
  );
}

const th = { padding: "12px 16px", textAlign: "left", fontSize: 13, fontWeight: 500, color: "var(--text-muted)", borderBottom: "1px solid var(--border)", verticalAlign: "top" };
const td = { padding: "12px 16px", fontSize: 14, borderBottom: "1px solid var(--border)", verticalAlign: "top" };

function Row({ label, children }) {
  return (
    <tr>
      <td style={{ ...td, fontWeight: 500, color: "var(--text)" }}>{label}</td>
      {children}
    </tr>
  );
}
function RowHeader({ label }) {
  return (
    <tr>
      <td colSpan={4} style={{ ...td, padding: "16px 16px 6px", fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--primary)", background: "transparent", borderBottom: "none" }}>{label}</td>
    </tr>
  );
}
function ReadRow({ label, cells, bold }) {
  return (
    <tr>
      <td style={{ ...td, color: "var(--text-muted)" }}>{label}</td>
      {cells.map((c, i) => (
        <td key={i} style={{ ...td, fontWeight: bold ? 600 : 400 }}>{c}</td>
      ))}
    </tr>
  );
}
